/**
 * Screen 1 — qualification.
 *
 * Five questions, then the rate and contract terms. The prototype drove this
 * with nested callbacks; here the questions are data and the screen renders the
 * current one, which is what makes the flow readable and the copy editable
 * without touching control flow.
 *
 * The "Avrio is typing" pause is theatre and is skipped entirely under
 * prefers-reduced-motion, where an artificial delay is just latency.
 *
 * ONE DELIBERATE DEVIATION FROM THE PROTOTYPE, for Product to rule on.
 * The prototype compared contract value against a $5,475 industry
 * cost-per-hire benchmark and showed the saving as a headline percentage. That
 * figure is computed here (`quote.benchmarkSaving`) but is NOT rendered,
 * because the two numbers are not like for like: $5,475 is a fully loaded
 * cost-per-hire including recruiter salaries, job boards, tooling and interview
 * time, while our rate buys matching. Presenting "93% cheaper" from that
 * comparison is a claim we could not defend in a renewal conversation.
 *
 * What is rendered instead is the comparison against the spend the prospect
 * themselves gave us, which is their number and roughly the right scope. If
 * Product wants the benchmark line back, it needs to say what the benchmark
 * includes, and the value is already available to render.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input } from 'antd';
import FlowShell from '../../components/flow-shell';
import { useSelfServe } from '../../context';
import {
  CurrentTool,
  QualificationAnswers,
  RoleType,
  Volatility,
  formatUsd,
  parseCount,
  RATE_CARD_SIGNED_OFF,
  MAX_SELF_SERVE_HIRES,
} from '../../utils/quote';
import { contactHref } from '../../utils/contact';
import styles from './index.module.css';
import FlexQualify from './flex';

const TYPING_MS = 420;

type Answers = Partial<QualificationAnswers>;

interface Choice {
  label: string;
  apply: (a: Answers) => Answers;
}

type Question =
  | {
      kind: 'count';
      id: string;
      /** May depend on answers already given. */
      prompt: (a: Answers) => string;
      placeholder: string;
      retry: string;
      /** A reply that asks again, or null to accept n. */
      outOfRange?: (n: number) => string | null;
      apply: (a: Answers, n: number) => Answers;
      skippable?: false;
    }
  | {
      kind: 'optionalCount';
      id: string;
      prompt: (a: Answers) => string;
      placeholder: string;
      apply: (a: Answers, n: number | null) => Answers;
    }
  | {
      kind: 'choice';
      id: string;
      prompt: (a: Answers) => string;
      choices: Choice[];
    };

const QUESTIONS: Question[] = [
  {
    kind: 'count',
    id: 'hires',
    prompt: () =>
      'First — roughly how many people do you expect to hire in the next 12 months?',
    placeholder: 'e.g. 120',
    retry: 'Just need a rough number — even an estimate like 50 or 200 works.',
    outOfRange: (n) =>
      n > MAX_SELF_SERVE_HIRES
        ? `Self-serve covers up to ${MAX_SELF_SERVE_HIRES.toLocaleString(
            'en-US',
          )} hires a year, so ${n.toLocaleString(
            'en-US',
          )} needs a conversation with our team rather than this form. Use the link below to reach us, or if part of that hiring is a smaller, separate program, enter that number instead.`
        : null,
    apply: (a, n) => ({ ...a, hires: n }),
  },
  {
    kind: 'choice',
    id: 'volatility',
    prompt: (a) =>
      `Got it — around ${a.hires} hires. Is that fairly steady through the year, or does it come in bursts: a seasonal peak, a new location, a project ramp?`,
    choices: [
      {
        label: 'Pretty steady',
        apply: (a) => ({ ...a, volatility: 'steady' as Volatility }),
      },
      {
        label: 'Comes in bursts',
        apply: (a) => ({ ...a, volatility: 'bursty' as Volatility }),
      },
    ],
  },
  {
    kind: 'choice',
    id: 'roleType',
    prompt: () =>
      'What kind of roles make up most of that hiring, by typical pay level?',
    choices: [
      {
        label: 'Office / corporate',
        apply: (a) => ({ ...a, roleType: 'office' as RoleType }),
      },
      {
        label: 'Retail / hourly',
        apply: (a) => ({ ...a, roleType: 'hourly' as RoleType }),
      },
      {
        label: 'Skilled trades / technical',
        apply: (a) => ({ ...a, roleType: 'trades' as RoleType }),
      },
      {
        label: 'Leadership (VP and above)',
        apply: (a) => ({ ...a, roleType: 'leadership' as RoleType }),
      },
    ],
  },
  {
    kind: 'choice',
    id: 'tool',
    prompt: () => 'And what are you mainly using to hire today?',
    choices: [
      {
        label: 'In-house recruiters',
        apply: (a) => ({ ...a, tool: 'inhouse' as CurrentTool }),
      },
      {
        label: 'LinkedIn Recruiter seats',
        apply: (a) => ({ ...a, tool: 'linkedin' as CurrentTool }),
      },
      {
        label: 'Staffing agencies',
        apply: (a) => ({ ...a, tool: 'agency' as CurrentTool }),
      },
      {
        label: 'A mix of these',
        apply: (a) => ({ ...a, tool: 'mix' as CurrentTool }),
      },
    ],
  },
  {
    kind: 'optionalCount',
    id: 'spend',
    prompt: () =>
      "Last one — roughly what do you spend per year on that today? Fine to skip if you don't have a number handy.",
    placeholder: 'e.g. 60000',
    apply: (a, n) => ({ ...a, annualSpend: n }),
  },
];

interface Turn {
  from: 'avrio' | 'you';
  text: string;
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const DownPaymentQualify = () => {
  const navigate = useNavigate();
  const { quote, completeQualification } = useSelfServe();

  const [answers, setAnswers] = useState<Answers>({});
  const [index, setIndex] = useState(0);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [typing, setTyping] = useState(true);
  const [draft, setDraft] = useState('');
  const [retryFor, setRetryFor] = useState<string | null>(null);
  /** The volume last refused for being above self-serve, for the contact link. */
  const [overCeiling, setOverCeiling] = useState<number | null>(null);

  const threadRef = useRef<HTMLDivElement>(null);
  const timers = useRef<number[]>([]);

  const question: Question | undefined = QUESTIONS[index];

  /** Queue a bot turn, with the typing pause unless motion is reduced. */
  const say = useCallback((text: string) => {
    if (prefersReducedMotion()) {
      setTurns((t) => [...t, { from: 'avrio', text }]);
      setTyping(false);
      return;
    }
    setTyping(true);
    const id = window.setTimeout(() => {
      setTurns((t) => [...t, { from: 'avrio', text }]);
      setTyping(false);
    }, TYPING_MS);
    timers.current.push(id);
  }, []);

  // Clear pending timers on unmount so a navigation mid-pause does not set
  // state on a gone component.
  useEffect(
    () => () => {
      timers.current.forEach((id) => window.clearTimeout(id));
    },
    [],
  );

  // Ask the current question whenever we advance to it.
  useEffect(() => {
    if (!question) return;
    say(question.prompt(answers));
    // answers is deliberately not a dependency: prompts read it at ask time,
    // and re-asking on every answer change would duplicate turns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, question, say]);

  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns, typing]);

  const advance = (next: Answers) => {
    setAnswers(next);
    setRetryFor(null);
    setOverCeiling(null);
    if (index + 1 >= QUESTIONS.length) {
      completeQualification(next as QualificationAnswers);
      setIndex(QUESTIONS.length);
      return;
    }
    setIndex(index + 1);
  };

  const submitCount = () => {
    if (
      !question ||
      (question.kind !== 'count' && question.kind !== 'optionalCount')
    )
      return;
    const raw = draft.trim();
    if (!raw) return;
    setTurns((t) => [...t, { from: 'you', text: raw }]);
    setDraft('');
    const n = parseCount(raw);
    if (n === null) {
      if (question.kind === 'count') {
        setRetryFor(question.id);
        say(question.retry);
        return;
      }
      advance(question.apply(answers, null));
      return;
    }
    const rejection =
      question.kind === 'count' && question.outOfRange
        ? question.outOfRange(n)
        : null;
    if (rejection) {
      setRetryFor(question.id);
      setOverCeiling(n);
      say(rejection);
      return;
    }
    advance(question.apply(answers, n));
  };

  const skip = () => {
    if (!question || question.kind !== 'optionalCount') return;
    setTurns((t) => [...t, { from: 'you', text: 'Skip — not sure' }]);
    advance(question.apply(answers, null));
  };

  const choose = (choice: Choice) => {
    setTurns((t) => [...t, { from: 'you', text: choice.label }]);
    advance(choice.apply(answers));
  };

  const awaitingCount =
    !typing &&
    question &&
    (question.kind === 'count' || question.kind === 'optionalCount');
  const awaitingChoice = !typing && question && question.kind === 'choice';

  return (
    <FlowShell
      step={1}
      title="Get your rate and contract terms"
      subtitle="About two minutes. No sales call required."
    >
      <div className={styles.thread} ref={threadRef} aria-live="polite">
        {turns.map((turn, i) => (
          <p
            key={`${turn.from}-${i}`}
            className={
              turn.from === 'avrio' ? styles.fromAvrio : styles.fromYou
            }
          >
            {turn.text}
          </p>
        ))}
        {typing && (
          <p className={styles.typing} aria-hidden="true">
            Avrio is typing…
          </p>
        )}
      </div>

      {awaitingChoice && question.kind === 'choice' && (
        <div className={styles.choices}>
          {question.choices.map((c) => (
            <Button
              key={c.label}
              size="large"
              onClick={() => choose(c)}
              className={styles.choice}
            >
              {c.label}
            </Button>
          ))}
        </div>
      )}

      {awaitingCount && (
        <div className={styles.inputRow}>
          {/* Large, numeric and "send": every answer here is a count, so a phone
              shows the number pad, and its return key reads as sending. */}
          <Input
            size="large"
            inputMode="numeric"
            enterKeyHint="send"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onPressEnter={submitCount}
            placeholder={
              question.kind === 'count' || question.kind === 'optionalCount'
                ? question.placeholder
                : ''
            }
            aria-label={retryFor ? 'Your answer, again' : 'Your answer'}
            autoFocus
          />
          <Button
            type="primary"
            size="large"
            onClick={submitCount}
            disabled={!draft.trim()}
          >
            Send
          </Button>
          {question.kind === 'optionalCount' && (
            <Button
              type="text"
              size="large"
              onClick={skip}
              className={styles.skip}
            >
              Skip
            </Button>
          )}
        </div>
      )}

      {overCeiling !== null && (
        <p className={styles.note}>
          <a
            href={contactHref(
              `Avrio for ${overCeiling.toLocaleString('en-US')} hires a year`,
              `We expect to hire around ${overCeiling.toLocaleString(
                'en-US',
              )} people in the next 12 months, which is above what self-serve covers. We'd like to talk about working with Avrio.`,
            )}
          >
            Talk to Avrio about hiring at this volume
          </a>
        </p>
      )}

      {quote && (
        <div className={styles.summary}>
          <h2 className={styles.summaryTitle}>
            {RATE_CARD_SIGNED_OFF ? 'Your rate' : 'Your indicative rate'}
          </h2>
          <dl className={styles.figures}>
            <div>
              <dt>Hires a year</dt>
              <dd>{quote.hires.toLocaleString('en-US')}</dd>
            </div>
            <div>
              <dt>Rate per hire</dt>
              <dd>{formatUsd(quote.ratePerHire)}</dd>
            </div>
            <div>
              <dt>Contract value</dt>
              <dd>{formatUsd(quote.contractValue)}</dd>
            </div>
            <div>
              <dt>Due up front</dt>
              <dd>{formatUsd(quote.downPayment)}</dd>
            </div>
            <div>
              <dt>Drawn down as you hire</dt>
              <dd>{formatUsd(quote.drawDown)}</dd>
            </div>
          </dl>

          {quote.ownSpendDifference !== null &&
            quote.ownSpendDifference > 0 && (
              <p className={styles.note}>
                That is {formatUsd(quote.ownSpendDifference)} below the{' '}
                {formatUsd(quote.ownSpendDifference + quote.contractValue)} a
                year you told us you spend today.
              </p>
            )}

          {answers.roleType === 'leadership' && (
            <p className={styles.note}>
              Executive and C-suite searches are quoted individually, starting
              from $100,000 per search, rather than priced on this per-hire
              card. Reach out directly if that is what you are hiring for.
            </p>
          )}

          <p className={styles.caveat}>
            {RATE_CARD_SIGNED_OFF
              ? 'A fixed rate per hire, set by role level. Try it free on a job description first; the amount due up front is paid by card when you activate, and the rest is drawn down as you hire.'
              : 'Rate shown is indicative; the final rate card is pending sign-off.'}
          </p>

          <Button
            type="primary"
            size="large"
            onClick={() => navigate('/selfserve/account')}
            className={styles.cta}
          >
            Continue
          </Button>
        </div>
      )}
    </FlowShell>
  );
};

/** The backend's flow decides which first screen a prospect sees (config.ts). */
const Qualify = () => {
  const { cardOnFile } = useSelfServe();
  return cardOnFile ? <FlexQualify /> : <DownPaymentQualify />;
};

export default Qualify;
